import { 
  Box, 
  Button, 
  Grid, 
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
  Divider,
  Card,
  CardContent
} from '@mui/material'
import React, { useState, useEffect } from 'react'
import Daterange from '../FinancialReport/Daterange'
import { 
  Search, 
  ExpandMore,
  Warning,
  Cloud,
  Storage,
  Computer,
  CalendarToday,
  AccessTime,
  CheckCircle,
  Cancel,
  Refresh,
  Info,
  TrendingUp
} from '@mui/icons-material'
import axios from 'axios'
import { getAxiosConfig } from '../../../helpers/AuthHelper'
import TableLoader from '../GlobalComponents/TableLoader'

// Type definitions for API response
interface DailyDiscrepancyDto {
  date: string;
  segpool: string[];
  cloud: string[];
  equalizer: string[];
  unconfiguredmachines: string[];
}

interface FinancialDiscrepancyResponseDto {
  timeTakenScanTheReport: string;
  status: string;
  totalDaysScanned: number;
  startingDate: string;
  endingDate: string;
  verified: boolean;
  summary: DailyDiscrepancyDto[];
}

export default function ValidatorControl() {
  // State for date range
    const getYesterday = (): Date => {
    const date = new Date();
    date.setDate(date.getDate() - 1);
    date.setHours(23, 59, 59, 999); // End of yesterday
    return date;
    };

    const [fromDate, setFromDate] = useState<Date | null>(getYesterday());
    const [toDate, setToDate] = useState<Date | null>(getYesterday());
    const [searchTrigger, setSearchTrigger] = useState<boolean>(false);
    
    // State for API response data
    const [discrepancyData, setDiscrepancyData] = useState<FinancialDiscrepancyResponseDto | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false);

    const handleSetFromDate = (date: Date | null) => {
        const yesterday = getYesterday();
        if (date && date > yesterday) {
            setFromDate(yesterday);
        } else {
            setFromDate(date);
        }
        };

    const handleSetToDate = (date: Date | null) => {
        const yesterday = getYesterday();
        if (date && date > yesterday) {
            setToDate(yesterday);
        } else {
            setToDate(date);
        }
        };

  // Function to format date as YYYY-MM-DD
  const formatDateForAPI = (date: Date | null): string => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Format date for display
  const formatDateForDisplay = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Function to fetch discrepancy report
  const fetchDiscrepancyReport = (): void => {
    setLoading(true);
    setError(null);
    
    // Format dates for API request
    const startDateParam = formatDateForAPI(fromDate);
    const endDateParam = formatDateForAPI(toDate);
    
    axios.get<FinancialDiscrepancyResponseDto>('/api/pool/financial/discrepancy/report', {
      params: {
        startDate: startDateParam,
        endDate: endDateParam
      },
      // Add your getAxiosConfig() here if needed
      ...getAxiosConfig()
    })
    .then(response => {
      setDiscrepancyData(response.data);
      setLoading(false);
    })
    .catch(error => {
      setError(error.message || 'Failed to fetch discrepancy report');
      setLoading(false);
      console.error('Error fetching discrepancy report:', error);
    });
  };

  // Handle accordion change
  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  // Calculate summary metrics
  const getSummaryMetrics = () => {
    if (!discrepancyData) return null;
    
    const totalSegpool = discrepancyData.summary.reduce((acc, day) => acc + day.segpool.length, 0);
    const totalCloud = discrepancyData.summary.reduce((acc, day) => acc + day.cloud.length, 0);
    const totalUnconfigured = discrepancyData.summary.reduce((acc, day) => 
      acc + day.unconfiguredmachines.filter(item => item !== "null").length, 0
    );
    
    return { totalSegpool, totalCloud, totalUnconfigured };
  };

  // Trigger API call when searchTrigger changes or on component mount
  useEffect(() => {
    if (searchTrigger || fromDate || toDate) {
      fetchDiscrepancyReport();
    }
  }, [searchTrigger]);

  // Optional: Fetch data on initial load with default dates
  useEffect(() => {
    fetchDiscrepancyReport();
  }, []); // Empty dependency array means run once on mount

  // Handle search button click
  const handleSearchClick = (): void => {
    setSearchTrigger(prev => !prev);
  };

  // Handle refresh button click
  const handleRefreshClick = (): void => {
    fetchDiscrepancyReport();
  };

  const summaryMetrics = getSummaryMetrics();

  return (
    <Box sx={{ 
      flexGrow: 1, 
      p: 3, 
      position: 'relative', 
      minHeight: 'calc(100vh - 64px)',
      backgroundColor: '#f8fafc'
    }}>
    <Box
        display="flex"
        position="sticky"
        top="70px"
        zIndex={100}
        flexDirection={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        gap={1.5}
        sx={{
            mb: 2,
            backgroundColor: '#d1e8ff',
            px: { xs: 1.5, sm: 2 },
            py: 1.5,
            borderRadius: 1,
        }}
        >
        {/* Left: Title */}
        <Typography
            variant="h5"
            sx={{
            fontFamily: 'monospace',
            color: '#1565c0',
            textAlign: { xs: 'center', sm: 'left' },
            whiteSpace: 'nowrap',
            }}
        >
            Finance Validator
        </Typography>

        {/* Right: Date range + Search */}
        <Box
            display="flex"
            alignItems="center"
            justifyContent={{ xs: 'center', sm: 'flex-end' }}
            gap={1.5}
            flexWrap="wrap"
        >
            <Daterange
                fromDate={fromDate}
                toDate={toDate}
                setFromDate={handleSetFromDate}
                setToDate={handleSetToDate}
                setSearchTrigger={setSearchTrigger}
                searchTrigger={searchTrigger}
                />

            <Button
                variant="contained"
                size="small"
                onClick={handleSearchClick}
                disabled={loading}
                startIcon={<Search />}
                >
                {loading ? 'Searching…' : 'Search'}
            </Button>
        </Box>
    </Box>



      {/* Loading State */}
      {loading && (
          <TableLoader/>
      )}

      {/* Error State */}
      {error && !loading && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3, 
            borderRadius: 3,
            '& .MuiAlert-icon': { fontSize: 30 }
          }}
        >
          <Typography variant="h6" gutterBottom>
            Failed to Load Data
          </Typography>
          <Typography>{error}</Typography>
          <Button 
            variant="outlined" 
            onClick={handleRefreshClick}
            sx={{ mt: 2 }}
          >
            Try Again
          </Button>
        </Alert>
      )}

      {/* Data Display */}
      {discrepancyData && !loading && !error && (
        <>
        <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={3}>
            <Paper
            sx={{
                p: 3,
                borderRadius: 3,
                height: '100%',
                background: 'linear-gradient(135deg, #f8fbff 0%, #f0f7ff 100%)',
                border: '1px solid',
                borderColor: 'primary.100',
                transition: 'all 0.3s ease',
                '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 24px rgba(33, 150, 243, 0.15)',
                borderColor: 'primary.300',
                }
            }}
            >
            <Box display="flex" alignItems="center" gap={2.5}>
                <Box
                sx={{
                    p: 2,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)',
                }}
                >
                <Storage sx={{ color: 'white', fontSize: 24 }} />
                </Box>
                <Box>
                <Typography variant="h4" fontWeight={800} sx={{ lineHeight: 1, color: 'primary.900' }}>
                    {summaryMetrics?.totalSegpool || 0}
                </Typography>
                <Typography variant="body2" color="primary.700" fontWeight={600} sx={{ mt: 0.5 }}>
                    SegPool Issues
                </Typography>
                </Box>
            </Box>
            </Paper>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
            <Paper
            sx={{
                p: 3,
                borderRadius: 3,
                height: '100%',
                background: 'linear-gradient(135deg, #f8fbff 0%, #f0f7ff 100%)',
                border: '1px solid',
                borderColor: 'primary.100',
                transition: 'all 0.3s ease',
                '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 24px rgba(33, 150, 243, 0.15)',
                borderColor: 'primary.300',
                }
            }}
            >
            <Box display="flex" alignItems="center" gap={2.5}>
                <Box
                sx={{
                    p: 2,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)',
                }}
                >
                <Cloud sx={{ color: 'white', fontSize: 24 }} />
                </Box>
                <Box>
                <Typography variant="h4" fontWeight={800} sx={{ lineHeight: 1, color: 'primary.900' }}>
                    {summaryMetrics?.totalCloud || 0}
                </Typography>
                <Typography variant="body2" color="primary.700" fontWeight={600} sx={{ mt: 0.5 }}>
                    Cloud Issues
                </Typography>
                </Box>
            </Box>
            </Paper>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
            <Paper
            sx={{
                p: 3,
                borderRadius: 3,
                height: '100%',
                background: 'linear-gradient(135deg, #f8fbff 0%, #f0f7ff 100%)',
                border: '1px solid',
                borderColor: 'primary.100',
                transition: 'all 0.3s ease',
                '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 24px rgba(33, 150, 243, 0.15)',
                borderColor: 'primary.300',
                }
            }}
            >
            <Box display="flex" alignItems="center" gap={2.5}>
                <Box
                sx={{
                    p: 2,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)',
                }}
                >
                <Computer sx={{ color: 'white', fontSize: 24 }} />
                </Box>
                <Box>
                <Typography variant="h4" fontWeight={800} sx={{ lineHeight: 1, color: 'primary.900' }}>
                    {summaryMetrics?.totalUnconfigured || 0}
                </Typography>
                <Typography variant="body2" color="primary.700" fontWeight={600} sx={{ mt: 0.5 }}>
                    Unconfigured
                </Typography>
                </Box>
            </Box>
            </Paper>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
            <Paper
            sx={{
                p: 3,
                borderRadius: 3,
                height: '100%',
                background: 'linear-gradient(135deg, #f8fbff 0%, #f0f7ff 100%)',
                border: '1px solid',
                borderColor: 'primary.100',
                transition: 'all 0.3s ease',
                '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 24px rgba(33, 150, 243, 0.15)',
                borderColor: 'primary.300',
                }
            }}
            >
            <Box display="flex" alignItems="center" gap={2.5}>
                <Box
                sx={{
                    p: 2,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)',
                }}
                >
                <AccessTime sx={{ color: 'white', fontSize: 24 }} />
                </Box>
                <Box>
                <Typography variant="h4" fontWeight={800} sx={{ lineHeight: 1, color: 'primary.900' }}>
                    {discrepancyData?.totalDaysScanned || 0}
                </Typography>
                <Typography variant="body2" color="primary.700" fontWeight={600} sx={{ mt: 0.5 }}>
                    Days Scanned
                </Typography>
                </Box>
            </Box>
            </Paper>
        </Grid>
        </Grid>


          {/* Report Header */}
        <Paper
            elevation={0}
            sx={{
                p: 3,
                borderRadius: 2,
                mb: 4,
                backgroundColor: 'grey.50',
                border: '1px solid',
                borderColor: 'divider',
            }}
            >
            <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" gap={2}>
                <Box>
                <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 600, mb: 1 }}>
                    📊 Report Summary
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AccessTime sx={{ fontSize: 14 }} />
                    {discrepancyData.timeTakenScanTheReport}
                    </Typography>
                    <Typography 
                    variant="body2" 
                    sx={{ 
                        color: discrepancyData.verified ? 'success.main' : 'error.main',
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 0.5 
                    }}
                    >
                    {discrepancyData.verified ? <CheckCircle sx={{ fontSize: 14 }} /> : <Cancel sx={{ fontSize: 14 }} />}
                    {discrepancyData.verified ? 'Verified' : 'Not Verified'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Info sx={{ fontSize: 14 }} />
                    {discrepancyData.totalDaysScanned} days
                    </Typography>
                </Box>
                </Box>
                
                <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                    Period
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                    {formatDateForDisplay(discrepancyData.startingDate)} → {formatDateForDisplay(discrepancyData.endingDate)}
                </Typography>
                </Box>
            </Box>
        </Paper>

         <Box sx={{ mt: 3 }}>
            <Box sx={{ textAlign: 'start', mb: 4 }}>
            <Typography 
                variant="h6" 
                sx={{ 
                color: 'primary.main',
                fontWeight: 700,
                letterSpacing: -0.5,
                mb: 1,
                ml:4,
                position: 'relative',
                display: 'inline-block',
                
                }}
            >
                Daily Discrepancy Breakdown
            </Typography>
            </Box>
            {discrepancyData.summary.map((daily, index) => (
                <Accordion
                key={index}
                expanded={expandedAccordion === `panel${index}`}
                onChange={handleAccordionChange(`panel${index}`)}
                elevation={0}
                sx={{
                    mb: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'white',
                    '&:before': { display: 'none' },
                    overflow: 'hidden',
                }}
                >
                <AccordionSummary
                    expandIcon={<ExpandMore sx={{ color: 'primary.main' }} />}
                    sx={{
                    minHeight: 68,
                    '&.Mui-expanded': { minHeight: 68 },
                    backgroundColor: expandedAccordion === `panel${index}` ? 'grey.50' : 'white',
                    borderBottom: expandedAccordion === `panel${index}` ? '1px solid' : 'none',
                    borderBottomColor: 'divider',
                    }}
                >
                    <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 2 }}>
                    {/* Date */}
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', minWidth: '140px' }}>
                        {formatDateForDisplay(daily.date)}
                    </Typography>

                    {/* Category Indicators */}
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Storage sx={{ fontSize: 18, color: daily.segpool.length > 0 ? 'warning.main' : 'grey.400' }} />
                        <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                            {daily.segpool.length} SegPool
                        </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Cloud sx={{ fontSize: 18, color: daily.cloud.length > 0 ? 'primary.main' : 'grey.400' }} />
                        <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                            {daily.cloud.length} Cloud
                        </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Cloud sx={{ fontSize: 18, color: daily.equalizer.length > 0 ? 'primary.main' : 'grey.400' }} />
                        <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                            {daily.equalizer.length} Equalizer
                        </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Computer sx={{ fontSize: 18, color: daily.unconfiguredmachines.filter(item => item !== "null").length > 0 ? 'secondary.main' : 'grey.400' }} />
                        <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                            {daily.unconfiguredmachines.filter(item => item !== "null").length} Unconfigured
                        </Typography>
                        </Box>
                    </Box>
                    </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* SegPool Section */}
                    {daily.segpool.length > 0 && (
                        <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'warning.main', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Storage />
                            SegPool Discrepancies
                            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'warning.main', fontWeight: 500 }}>
                            ({daily.segpool.length} items)
                            </Typography>
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pl: 3 }}>
                            {daily.segpool.map((item, i) => (
                            <Chip
                                key={i}
                                label={item}
                                size="small"
                                sx={{
                                backgroundColor: 'warning.50',
                                color: 'warning.900',
                                fontFamily: 'monospace',
                                }}
                            />
                            ))}
                        </Box>
                        </Box>
                    )}

                    {/* Cloud Section */}
                    {daily.cloud.length > 0 && (
                        <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Cloud />
                            Cloud Discrepancies
                            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'primary.main', fontWeight: 500 }}>
                            ({daily.cloud.length} items)
                            </Typography>
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pl: 3 }}>
                            {daily.cloud.map((item, i) => (
                            <Chip
                                key={i}
                                label={item}
                                size="small"
                                sx={{
                                backgroundColor: 'primary.50',
                                color: 'primary.900',
                                fontFamily: 'monospace',
                                }}
                            />
                            ))}
                        </Box>
                        </Box>
                    )}

                    {/* Equalizer Section */}
                    {daily.equalizer.length > 0 && (
                        <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Cloud />
                            Equalizer Discrepancies
                            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'primary.main', fontWeight: 500 }}>
                            ({daily.equalizer.length} items)
                            </Typography>
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pl: 3 }}>
                            {daily.equalizer.map((item, i) => (
                            <Chip
                                key={i}
                                label={item}
                                size="small"
                                sx={{
                                backgroundColor: 'primary.50',
                                color: 'primary.900',
                                fontFamily: 'monospace',
                                }}
                            />
                            ))}
                        </Box>
                        </Box>
                    )}

                    {/* Unconfigured Section */}
                    {daily.unconfiguredmachines.filter(item => item !== "null").length > 0 && (
                        <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'secondary.main', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Computer />
                            Unconfigured Machines
                            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'secondary.main', fontWeight: 500 }}>
                            ({daily.unconfiguredmachines.filter(item => item !== "null").length} items)
                            </Typography>
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pl: 3 }}>
                            {daily.unconfiguredmachines
                            .filter(item => item !== "null")
                            .map((item, i) => (
                                <Chip
                                key={i}
                                label={item}
                                size="small"
                                sx={{
                                    backgroundColor: 'secondary.50',
                                    color: 'secondary.900',
                                    fontFamily: 'monospace',
                                }}
                                />
                            ))}
                        </Box>
                        </Box>
                    )}

                    {/* Empty State */}
                    {daily.segpool.length === 0 && daily.cloud.length === 0 && daily.equalizer.length === 0 &&
                    daily.unconfiguredmachines.filter(item => item !== "null").length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 3 }}>
                        <CheckCircle sx={{ fontSize: 40, color: 'success.main', mb: 1.5, opacity: 0.8 }} />
                        <Typography variant="subtitle1" sx={{ color: 'success.main', fontWeight: 500 }}>
                            No discrepancies detected
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                            All systems are properly synchronized
                        </Typography>
                        </Box>
                    )}
                    </Box>
                </AccordionDetails>
                </Accordion>
            ))}
        </Box>
        </>
      )}
    </Box>
  );
}